import { ReactNode } from 'react';
import PrintOrDistributeDocuments from './PrintOrDistributeDocuments';
import { Box, Grid, TextareaAutosize, Typography } from '@mui/material';
import UploadDocument from './uploadDocument/UploadDocument';
import Attestation from './Attestation';
import { useSelector } from 'react-redux';
import ServiceRoleFormWithPartnerSearch from 'components/productConfiguration/ServiceRoleFormWithPartnerSearch';
import { RootState } from 'store';
import { StepMetadata } from 'pco-product-service-internal-consumer';

type CustomStepMetadataProps = {
  closeAction: () => void;
  isReadOnly: boolean;
  customStepMetadata:
    | (Partial<StepMetadata> & Partial<{ componentProps?: string | undefined; customComponent?: string | undefined }>)
    | undefined;
};

const CustomStepMetadata = ({ closeAction, isReadOnly, customStepMetadata }: CustomStepMetadataProps) => {
  const { taskId, componentProps, customComponent } = customStepMetadata ?? {
    componentName: null,
    taskId: null,
    componentProps: null,
  };
  const stepMetadataProperties = componentProps ? JSON.parse(componentProps) : null;
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const exitingTask = selectedServiceAgreement?.tasks?.find((t) => t.taskName === 'Attestation');
  const existingTaskConfig = isReadOnly ? (exitingTask ? JSON.parse(exitingTask?.content ?? '') : null) : null;

  const getComponent = () => {
    let component: ReactNode;

    switch (customComponent) {
      case 'UploadDocumentAndDistributeNoCheckbox':
        if (taskId) {
          component = (
            <UploadDocument
              hasLanguages={true}
              formCodeList={stepMetadataProperties?.formCodeList}
              closeAction={closeAction}
              taskId={taskId}
            />
          );
        }
        break;

      case 'UploadDocumentAndDistribute':
        if (taskId) {
          component = (
            <UploadDocument
              closeAction={closeAction}
              hasLanguages={false}
              hasCheckboxes={true}
              taskId={taskId}
              formCodeList={stepMetadataProperties?.formCodeList}
            />
          );
        }
        break;

      case 'ServiceRoleSelection':
        component = (
          <>
            {!isReadOnly && stepMetadataProperties?.comment && (
              <Grid item xs={12}>
                <Typography>Comment</Typography>
                <TextareaAutosize
                  value={stepMetadataProperties?.comment}
                  minRows={4}
                  style={{
                    width: '100%',
                    backgroundColor: stepMetadataProperties?.comment ? 'lightgray' : 'white',
                    pointerEvents: stepMetadataProperties?.comment ? 'none' : 'auto',
                  }}
                />
              </Grid>
            )}
            <ServiceRoleFormWithPartnerSearch />
          </>
        );
        break;

      case 'UploadDocument':
        if (taskId) {
          component = (
            <UploadDocument
              closeAction={closeAction}
              taskId={taskId}
              formCodeList={stepMetadataProperties?.formCodeList}
            />
          );
        }
        break;

      case 'ChooseDocumentForDistributionRequest':
        if (taskId) {
          component = (
            <PrintOrDistributeDocuments
              isReadOnly={isReadOnly}
              taskId={taskId}
              documents={stepMetadataProperties}
              closeAction={closeAction}
            />
          );
        }
        break;

      case 'AttestationConfirmation':
        component = (
          <Attestation
            contactNoteIdProp={existingTaskConfig?.contactNoteId}
            checkedProp={existingTaskConfig?.checked}
            taskId={taskId}
            closeAction={closeAction}
            isReadOnly={isReadOnly}
            contactList={stepMetadataProperties?.contactList}
          />
        );
        break;
    }

    return component;
  };

  return (
    <Box data-testid="custom-step-metadata" sx={{ width: 'auto' }}>
      {getComponent()}
    </Box>
  );
};

export default CustomStepMetadata;
